
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to measure Link conversion'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Link analytics and tracking' subtitle='Learn how to understand Link analytics and measure conversion' mdxType="Header" />
    <h4 {...{
      "id": "link-analytics"
    }}>{`Link Analytics`}</h4>
    <p>{`Plaid instruments the actions users can take within the Link flow, allowing you to understand and measure a user's activity in Link. Pre-packaged summary views of some of the most important analytics are available within the Dashboard. `}</p>
    <Image src='/assets/img/docs/link-analytics-basic.png' caption="The standard Link analytics view in Dashboard" mdxType="Image" />
    <h5 {...{
      "id": "premium-link-analytics"
    }}>{`Premium Link Analytics`}</h5>
    <p>{`Customers on Premium Support Packages have access to Premium Link Analytics, which includes the following features:`}</p>
    <ul>
      <li parentName="ul">{`Segment conversion performance and top errors by geography, institution, device type, product, and Link customizations`}</li>
      <li parentName="ul">{`Compare against Plaid-wide benchmarks to identify strengths and opportunities`}</li>
      <li parentName="ul">{`Review the Link funnel step by step, including key actions like `}<inlineCode parentName="li">{`Institution Search`}</inlineCode>{` and `}<inlineCode parentName="li">{`Submit Credentials`}</inlineCode></li>
      <li parentName="ul">{`Compare OAuth vs. non-OAuth funnels`}</li>
      <li parentName="ul">{`View top Link errors as a percent of traffic`}</li>
      <li parentName="ul">{`View top 10 institutions by Link opens and their conversion performance`}</li>
    </ul>
    <p>{`To upgrade to a Premium Support Package, contact your Plaid account manager.`}</p>
    <Image src='/assets/img/docs/link-analytics-advanced.png' caption="The premium Link analytics view in Dashboard" expandable='true' mdxType="Image" />
    <h5 {...{
      "id": "auth-and-identity-match-conversion-analytics"
    }}>{`Auth and Identity Match conversion analytics`}</h5>
    <p>{`If you use Auth or Identity Match, you can also use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification/analytics"
      }}>{`Account Verification Analytics Dashboard`}</a>{` to view more detailed product-specific analytics, including a conversion breakdown by Auth flow. This Dashboard is available at no additional charge to all Auth customers.`}</p>
    <Image src='/assets/img/docs/auth/av_dashboard_analytics.png' alt='image of Account Verification analytics' expandable='true' mdxType="Image" />
    <h5 {...{
      "id": "link-analytics-via-mcp"
    }}>{`Link analytics via MCP`}</h5>
    <p>{`Support for Link analytics is available via Plaid's Dashboard MCP server, allowing you to analyze and interact with Link conversion data through chat or via an AI agent. For more details, see the `}<a parentName="p" {...{
        "href": "https://plaid.com/blog/plaid-mcp-ai-assistant-claude/"
      }}>{`blog post on the Plaid Dashboard MCP server`}</a>{` and the `}<a parentName="p" {...{
        "href": "/docs/resources/mcp/"
      }}>{`Plaid Dashboard MCP server documentation`}</a>{`.`}</p>
    <h4 {...{
      "id": "manual-link-analytics"
    }}>{`Manual Link analytics`}</h4>
    <p>{`To analyze Link activity in your own analytics platform, you can log and track Link actions yourself. This can be done either by tracking user activity on the frontend via the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{`, `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{`, and `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` callbacks, or via the backend, using the `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` endpoint. `}</p>
    <p>{`Link events are also tracked in the Dashboard via `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/logs"
      }}>{`Logs`}</a>{`. However, Logs is designed for troubleshooting and not recommended for analytics use cases, as it cannot easily be exported to a third-party analytics platform.`}</p>
    <p>{`This guide will go over the most important fields and events used for analytics, as well as the most popular analytics use cases. For a complete view of all user activity reporting that can be obtained from Link, see the documentation for the specific frontend platform SDK you are using (if tracking activity on the frontend) or for `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` (if tracking activity on the backend).`}</p>
    <Callout mdxType="Callout">
      <p>{`Link analytics activity names and structures will vary slightly depending on the platform you are using. For example, events fired by the Plaid iOS SDK use lowerCamelCase (e.g. `}<inlineCode parentName="p">{`selectInstitution`}</inlineCode>{`) while events fired by the Plaid web SDK or returned by `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` use `}<inlineCode parentName="p">{`SCREAMING_SNAKE`}</inlineCode>{` (e.g. `}<inlineCode parentName="p">{`SELECT_INSTITUTION`}</inlineCode>{`). `}</p>
      <p>{`Similarly, `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` returns an `}<inlineCode parentName="p">{`exit`}</inlineCode>{` object and an `}<inlineCode parentName="p">{`events`}</inlineCode>{` array rather than firing the callbacks `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` and `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{`. `}</p>
      <p>{`This guide will use the naming convention used by the web SDK. To see the exact naming syntax used by your platform, see the reference documentation for the specific SDK (or `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`).`}</p>
    </Callout>
    <h4 {...{
      "id": "tracking-errors-in-link"
    }}>{`Tracking errors in Link`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` callback is called when a user exits Link without successfully linking an Item, or when an error occurs during Link initialization. If the user encountered an error in the Link flow, the `}<inlineCode parentName="p">{`error`}</inlineCode>{` object will contain details of the most recently encountered error, including the `}<inlineCode parentName="p">{`error.error_code`}</inlineCode>{`, `}<inlineCode parentName="p">{`error.error_type`}</inlineCode>{`, and human-readable `}<inlineCode parentName="p">{`error.error_message`}</inlineCode>{`. `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sample error object",
        "switcher": "false",
        "#Sample": true,
        "error": true,
        "object": true
      }}>{`{
  error_type: 'ITEM_ERROR',
  error_code: 'INVALID_CREDENTIALS',
  error_message: 'the provided credentials were not correct',
  display_message: 'The provided credentials were not correct. Please try again.',
}
`}</code></pre>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link-analytics"
      }}>{`Link Analytics page`}</a>{` in the Plaid Dashboard will show the most common errors reported during the Link flow for your integration.`}</p>
    <h4 {...{
      "id": "tracking-abandons-in-link"
    }}>{`Tracking abandons in Link`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` callback is called when a user exits Link without successfully linking an Item, or when an error occurs during Link initialization. The `}<inlineCode parentName="p">{`metadata.status`}</inlineCode>{` field within `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` will reflect the point of the Link flow where the user abandoned the flow, and the `}<inlineCode parentName="p">{`metadata.institution`}</inlineCode>{` field will reflect which institution the user was attempting to connect to.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sample metadata object",
        "switcher": "false",
        "#Sample": true,
        "metadata": true,
        "object": true
      }}>{`{
  institution: {
    name: 'Citizens Bank',
    institution_id: 'ins_20'
  },
  status: 'requires_credentials',
  link_session_id: '36e201e0-2280-46f0-a6ee-6d417b450438',
  request_id: '8C7jNbDScC24THu'
}
`}</code></pre>
    <h4 {...{
      "id": "tracking-the-user-journey-in-link"
    }}>{`Tracking the user journey in Link`}</h4>
    <p>{`For almost every action your user takes within the Link flow, the `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` callback will fire, allowing you to track their progress through Link. The `}<inlineCode parentName="p">{`eventName`}</inlineCode>{` provided within `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` typically corresponds to an action the user has taken in Link or an outcome that has occurred. `}</p>
    <p>{`The most important event names include `}<inlineCode parentName="p">{`OPEN`}</inlineCode>{` (the user has opened Link), `}<inlineCode parentName="p">{`TRANSITION_VIEW`}</inlineCode>{` (the user has progressed to a new pane in Link),  `}<inlineCode parentName="p">{`HANDOFF`}</inlineCode>{` (the user has successfully completed Link), `}<inlineCode parentName="p">{`ERROR`}</inlineCode>{` (a recoverable error has occurred in Link), and `}<inlineCode parentName="p">{`EXIT`}</inlineCode>{` (the user has closed Link without completing it). `}</p>
    <p>{`The event names you should track will depend on your use case and the Plaid products you use. For a complete listing of event names, see the reference documentation for your SDK (or `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`).`}</p>
    <p>{`If using `}<a parentName="p" {...{
        "href": "/docs/link/embedded-institution-search/"
      }}>{`Embedded Institution Search (aka Embedded Link)`}</a>{`, note that viewing the Embedded Institution Search pane will not result in any Link events or analytics activity. Only when the end user interacts with Embedded Institution Search will events appear. `}</p>
    <Callout mdxType="Callout">
      <p>{`Complete event activity is only available for a Link session once it has finished. Plaid does not provide the ability to track the status of an active Link session in real time. Event callbacks are not guaranteed to fire in the order in which they occurred. If you need to determine the ordering of events, sort by the `}<inlineCode parentName="p">{`timestamp`}</inlineCode>{` field in the `}<inlineCode parentName="p">{`metadata`}</inlineCode>{` object.`}</p>
    </Callout>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Example series of events from a successful Link attempt",
        "switcher": "false",
        "#Example": true,
        "series": true,
        "of": true,
        "events": true,
        "from": true,
        "a": true,
        "successful": true,
        "Link": true,
        "attempt": true
      }}>{`OPEN
TRANSITION_VIEW
SELECT_INSTITUTION
TRANSITION_VIEW
SUBMIT_CREDENTIALS
TRANSITION_VIEW
HANDOFF
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Events from an unsuccessful Link attempt, wrong password entered repeatedly",
        "switcher": "false",
        "#Events": true,
        "from": true,
        "an": true,
        "unsuccessful": true,
        "Link": true,
        "attempt,": true,
        "wrong": true,
        "password": true,
        "entered": true,
        "repeatedly": true
      }}>{`OPEN
TRANSITION_VIEW
SELECT_INSTITUTION
TRANSITION_VIEW
SUBMIT_CREDENTIALS
TRANSITION_VIEW
ERROR
TRANSITION_VIEW
SUBMIT_CREDENTIALS
TRANSITION_VIEW
ERROR
TRANSITION_VIEW
EXIT
`}</code></pre>
    <h5 {...{
      "id": "view-names"
    }}>{`View names`}</h5>
    <p>{`For a more granular view of a user's experience in Link, you can also record the `}<inlineCode parentName="p">{`metadata.viewName`}</inlineCode>{` within `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{`. While the `}<inlineCode parentName="p">{`eventName`}</inlineCode>{` typically reflects the action taken by the user or the result of their action, the `}<inlineCode parentName="p">{`viewName`}</inlineCode>{` reflects the name of the specific Link pane being shown to the user, such as `}<inlineCode parentName="p">{`CONSENT`}</inlineCode>{` (asking the user to consent to the Plaid end user privacy policy) or `}<inlineCode parentName="p">{`OAUTH`}</inlineCode>{` (informing the user that they will be handed off to their institution's OAuth flow).`}</p>
    <h4 {...{
      "id": "measuring-link-conversion"
    }}>{`Measuring Link conversion`}</h4>
    <p>{`You can view your Link conversion by viewing the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link-analytics"
      }}>{`Observe > Link analytics page`}</a>{` in the Plaid Dashboard. If you use Auth or Identity Match, you can also use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification/analytics"
      }}>{`Account Verification Analytics Dashboard`}</a>{` to view more detailed product-specific analytics, including a conversion breakdown by Auth flow.`}</p>
    <p>{`Alternatively, you can set up your own conversion tracking. Tracking conversion on your own is recommended if you want more detailed analytics than reported in the Dashboard (for example, to perform A/B testing on Link conversion) or to connect Link conversion to your company's analytics platform.`}</p>
    <p>{`For tracking conversion, the most important events are `}<inlineCode parentName="p">{`HANDOFF`}</inlineCode>{`, which indicates that the user has linked an Item, and `}<inlineCode parentName="p">{`EXIT`}</inlineCode>{`, which indicates that the user has exited without linking an account. `}</p>
    <p>{`Your overall conversion rate is measured as the number of `}<inlineCode parentName="p">{`HANDOFF`}</inlineCode>{` events divided by the number of unique `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{`s. Alternatively, you can divide by the sum total of `}<inlineCode parentName="p">{`HANDOFF`}</inlineCode>{` and `}<inlineCode parentName="p">{`EXIT`}</inlineCode>{` events, but this method is less accurate, since an `}<inlineCode parentName="p">{`EXIT`}</inlineCode>{` event will not fire if a user destroys Link by quitting the browser or closing the tab. You can obtain insight into at what point a user abandoned Link by tracking the `}<inlineCode parentName="p">{`metadata.status`}</inlineCode>{` field within `}<inlineCode parentName="p">{`onExit`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Key funnel steps for OAuth institutions",
        "switcher": "false",
        "#Key": true,
        "funnel": true,
        "steps": true,
        "for": true,
        "OAuth": true,
        "institutions": true
      }}>{`...
SELECT_INSTITUTION
TRANSITION_VIEW (view_name = OAUTH)
OPEN_OAUTH
TRANSITION_VIEW (view_name = CONNECTED)
HANDOFF
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Key funnel steps for credentials-based institutions",
        "switcher": "false",
        "#Key": true,
        "funnel": true,
        "steps": true,
        "for": true,
        "credentials-based": true,
        "institutions": true
      }}>{`...
SELECT_INSTITUTION
TRANSITION_VIEW (view_name = CREDENTIAL)
SUBMIT_CREDENTIALS
TRANSITION_VIEW (view_name = MFA, mfa_type = code)
SUBMIT_MFA (mfa_type = code)
TRANSITION_VIEW (view_name = CONNECTED)
HANDOFF
`}</code></pre>
    <p>{`You can also capture the `}<inlineCode parentName="p">{`institution_name`}</inlineCode>{` field, provided by the `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` callback, to track which institutions your users are attempting to connect to.`}</p>
    <h5 {...{
      "id": "measuring-conversion-for-identity-verification"
    }}>{`Measuring conversion for Identity Verification`}</h5>
    <p>{`Because Identity Verification sessions trigger a unique set of events, you will need to use a slightly different method for calculating Identity Verification Link conversion, based on the `}<inlineCode parentName="p">{`IDENTITY_VERIFICATION_START_STEP`}</inlineCode>{`, `}<inlineCode parentName="p">{`IDENTITY_VERIFICATION_FAIL_SESSION`}</inlineCode>{` and `}<inlineCode parentName="p">{`IDENTITY_VERIFICATION_PASS_SESSION`}</inlineCode>{` events. For details, see `}<a parentName="p" {...{
        "href": "/docs/identity-verification/reporting"
      }}>{`Identity Verification Reporting`}</a>{`.`}</p>
    <h4 {...{
      "id": "analyzing-conversion-data"
    }}>{`Analyzing conversion data`}</h4>
    <p>{`Many customers use third-party analytics platforms to analyze conversion data, which can allow you to easily view data by platform or institution. Lower conversion on a specific platform or institution may indicate an implementation problem. For example, lower conversion on mobile for OAuth-supporting institutions may indicate an issue with the handling of `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#reinitializing-link"
      }}>{`OAuth redirects`}</a>{` or failure to implement `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#app-to-app-authentication"
      }}>{`app-to-app`}</a>{`.`}</p>
    <p>{`We recommend tracking conversion data over time to measure the impact of changes to your Link integration.`}</p>
    <h5 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h5>
    <p>{`Once you're measuring Link conversion, make sure you're maximizing it. For tips, see `}<a parentName="p" {...{
        "href": "/docs/link/best-practices"
      }}>{`Optimizing Link conversion`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "link-analytics",
        level: 4,
        title: "Link Analytics",
      },
{
        id: "manual-link-analytics",
        level: 4,
        title: "Manual Link analytics",
      },
{
        id: "tracking-errors-in-link",
        level: 4,
        title: "Tracking errors in Link",
      },
{
        id: "tracking-abandons-in-link",
        level: 4,
        title: "Tracking abandons in Link",
      },
{
        id: "tracking-the-user-journey-in-link",
        level: 4,
        title: "Tracking the user journey in Link",
      },
{
        id: "measuring-link-conversion",
        level: 4,
        title: "Measuring Link conversion",
      },
{
        id: "analyzing-conversion-data",
        level: 4,
        title: "Analyzing conversion data",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
